/**
 * Aplicação de Fontes Geradoras (#606): a configuração de fontes da tela única comum
 * (`InventarioDoDestino`). Diz o que é uma fonte, como a API aplica, edita e remove, e avisa que a
 * fonte não aplica o risco.
 */
import { Chip } from '@mui/material';

import { aplicarComResultadoPorItem } from '../../components/applications/resultadoPorItem';
import { linhaDeVoltaASeguir } from '../../components/applications/textosDoNivel';
import type {
  AcaoEmLote,
  ConfigDeAplicacao,
  ContextoDaRemocao,
  DestinoRef,
} from '../../components/applications/tipos';
import apiClient from '../../services/apiClient';
import { sourceApplicationService } from '../../services/sourceApplicationService';
import { sourceGeneratorService } from '../../services/sourceGeneratorService';
import {
  RISK_TYPE_LABELS,
  SourceGeneratorRiskType,
  SourceGeneratorStatus,
  type SourceGenerator,
} from '../../types/sourceGenerator';
import { SourceExposureLevel, type SourceWithApplication } from '../../types/sourceApplication';
import { AvisoDaFonte, DetalheDaFonte, InicioNaLinha, SeletorDeExposicao } from './camposDaFonte';
import { ROTULO_DA_EXPOSICAO, diaDe, eExposicao, eInicioCompleto, hoje, nomeDaAplicacao, type ValoresDaFonte } from './textosDaFonte';
import { ConferirAoMexer } from '../../components/applications/ConferirAoMexer';

export type { ValoresDaFonte } from './textosDaFonte';

/** O que só a cópia leva: a aplicação nova nasce sem fim e sem observações. */
interface CamposDaCopia {
  endDate: string | null;
  observations: string | null;
}

async function aplicarFontes(destino: DestinoRef, ids: number[], valores: ValoresDaFonte, copia?: CamposDaCopia) {
  return aplicarComResultadoPorItem(ids, 'sourceGeneratorId', () =>
    apiClient.post(`/source-applications/${destino.nivel}/${destino.id}/apply`, {
      sourceGeneratorIds: ids,
      exposureLevel: valores.exposureLevel,
      startDate: valores.startDate || null,
      ...copia,
    })
  );
}

const ACOES_EM_LOTE: AcaoEmLote<SourceWithApplication>[] = [
  {
    id: 'mudar-exposicao',
    rotulo: 'Mudar exposição',
    campo: 'exposicao',
    valorInicial: null,
    escolher: (valor, mudar) => (
      <SeletorDeExposicao
        valor={eExposicao(valor) ? valor : ''}
        rotulo="Exposição para os marcados"
        vazio="Escolha o nível"
        aoMudar={mudar}
      />
    ),
  },
];

/** Na linha recém-adicionada: exposição e início vieram do padrão, e ninguém deve aceitá-los sem olhar. */
function Confira() {
  return <Chip size="small" color="warning" variant="outlined" label="confira" sx={{ mt: 0.5 }} />;
}

function textoDeRemocao(
  aplicacoes: SourceWithApplication[],
  { destino, deCima }: ContextoDaRemocao<SourceWithApplication>
): string[] {
  const umaSo = aplicacoes.length === 1;
  return [
    `Apaga a exposição, as datas e as observações ${umaSo ? 'desta aplicação' : 'destas aplicações'}. Os riscos aplicados não mudam.`,
    `Documentos já emitidos não mudam; os próximos saem sem ${umaSo ? 'esta fonte' : 'estas fontes'}.`,
    ...deCima.map((herdada) =>
      linhaDeVoltaASeguir(nomeDaAplicacao(herdada), destino, herdada.origem, ROTULO_DA_EXPOSICAO[herdada.exposureLevel])
    ),
  ];
}

const ROTULO_DO_TIPO = Object.values(SourceGeneratorRiskType).map((tipo) => ({ valor: tipo, rotulo: RISK_TYPE_LABELS[tipo] }));

export const CONFIG_DE_FONTES: ConfigDeAplicacao<SourceGenerator, SourceWithApplication, ValoresDaFonte> = {
  familia: 'source',
  rotulos: {
    singular: 'fonte',
    plural: 'fontes',
    titulo: 'Aplicação de Fontes Geradoras',
    feminino: true,
    daTela: 'Fontes geradoras',
    confira: 'Confira a exposição.',
  },

  destaque: {
    aceita: (aplicacao) =>
      aplicacao.exposureLevel === SourceExposureLevel.HIGH || aplicacao.exposureLevel === SourceExposureLevel.VERY_HIGH,
    rotulo: (quantos) => `${quantos} com exposição alta`,
  },

  AvisoDosItens: AvisoDaFonte,

  carregarCatalogo: async () =>
    (await sourceGeneratorService.getSourceGenerators({ status: SourceGeneratorStatus.ACTIVE, limit: 2000 }))
      .sourceGenerators,
  item: {
    id: (fonte) => fonte.id,
    nome: (fonte) => fonte.name,
    descricao: (fonte) => fonte.description,
    perigo: (fonte) => fonte.location,
    tipo: (fonte) => fonte.riskType,
  },
  tipos: ROTULO_DO_TIPO,

  aplicacao: {
    id: (aplicacao) => aplicacao.id,
    itemId: (aplicacao) => aplicacao.sourceGeneratorId,
    nome: nomeDaAplicacao,
    descricao: (aplicacao) => aplicacao.location,
    tipo: (aplicacao) => aplicacao.riskType,
    periodo: (aplicacao) => aplicacao,
  },

  valoresIniciais: () => ({ exposureLevel: SourceExposureLevel.MEDIUM, startDate: hoje() }),
  chaveDoGrupo: (valores) => `${valores.exposureLevel}|${valores.startDate}`,
  aplicar: aplicarFontes,
  // Copia tudo o que a aplicação tem: é também como o "Desfazer" da remoção a recria.
  copiarDoHerdado: (destino, herdada) =>
    aplicarFontes(
      destino,
      [Number(herdada.sourceGeneratorId)],
      { exposureLevel: herdada.exposureLevel, startDate: diaDe(herdada.startDate) },
      { endDate: diaDe(herdada.endDate) || null, observations: herdada.observations ?? null }
    ),

  camposEditaveis: [
    {
      id: 'exposicao',
      rotulo: 'Exposição',
      ler: (aplicacao) => aplicacao.exposureLevel,
      render: (aplicacao, { gravar, somenteLeitura, recente }) =>
        somenteLeitura ? (
          <span>{ROTULO_DA_EXPOSICAO[aplicacao.exposureLevel]}</span>
        ) : (
          <ConferirAoMexer sugerido={recente === true} selo={<Confira />}>
            <SeletorDeExposicao
              valor={aplicacao.exposureLevel}
              rotulo={`Exposição de ${nomeDaAplicacao(aplicacao)}`}
              aoMudar={gravar}
            />
          </ConferirAoMexer>
        ),
    },
    {
      id: 'inicio',
      rotulo: 'Início',
      ler: (aplicacao) => diaDe(aplicacao.startDate),
      render: (aplicacao, { gravar, somenteLeitura, recente }) =>
        somenteLeitura ? (
          <span>{diaDe(aplicacao.startDate).split('-').reverse().join('/') || 'Sem data'}</span>
        ) : (
          <ConferirAoMexer sugerido={recente === true} selo={<Confira />}>
            <InicioNaLinha
              valor={diaDe(aplicacao.startDate)}
              fim={diaDe(aplicacao.endDate)}
              rotulo={`Início da exposição de ${nomeDaAplicacao(aplicacao)}`}
              aoMudar={gravar}
            />
          </ConferirAoMexer>
        ),
    },
  ],
  editar: async (destino, aplicacao, campo, valor) => {
    const fonteId = Number(aplicacao.sourceGeneratorId);
    if (campo === 'exposicao' && eExposicao(valor)) {
      await sourceApplicationService.updateForTarget(destino.nivel, destino.id, fonteId, { exposureLevel: valor });
    } else if (campo === 'inicio' && valor === '') {
      // Apagar o início é aplicar sem data de início.
      await sourceApplicationService.updateForTarget(destino.nivel, destino.id, fonteId, { startDate: null });
    } else if (campo === 'inicio' && typeof valor === 'string' && eInicioCompleto(valor)) {
      await sourceApplicationService.updateForTarget(destino.nivel, destino.id, fonteId, { startDate: valor });
    }
  },
  acoesEmLote: ACOES_EM_LOTE,
  JanelaDeDetalhe: DetalheDaFonte,
  remover: async (destino, aplicacoes) => {
    await Promise.all(
      aplicacoes.map((aplicacao) =>
        sourceApplicationService.removeFromTarget(destino.nivel, destino.id, Number(aplicacao.sourceGeneratorId))
      )
    );
  },
  textoDeRemocao,

  linkDaFicha: (destino) => (destino.nivel === 'occupation' ? `/occupations/${destino.id}` : null),
};
